import { useMutation, useQuery } from 'react-query'
import { UseQueryOptions } from 'react-query/types/react/types'
import axios, { AxiosError } from 'axios'

import { crNotJoined, ReporterFeedbackParticipantMapping } from '@/apiClient'
import { UploadedFile } from '@/components/common/Uploader'
import { AttendeeRole, SAVE_REPORTER_FEEDBACK_MUTATION_KEY } from '@/constants'
import {
  AsrWord,
  CrFormStepsState,
  Event,
  EventResult,
  Exhibit,
  JoinInfo,
  MediaResource,
  ReadWaiveAnswer,
  Reporter,
  SaScriptFields,
  TranscriptOrders,
} from '@/types'
import { getApiErrorMessage } from '@/utils/apiError'

export type CreateBookingPayload = {
  reporter_id: Reporter['id']
}

type BookEventError = {
  response?: { status?: number; data?: { detail?: unknown } }
}

type BookEventErrorOptions = {
  onReporterSettingsMissing?: () => void
  onReporterCommissionExpired?: () => void
  onBookingConflict?: () => void
  onErrorMessage: (error: string) => void
}

const PARROT_BOOKING_CONFLICT_DETAIL = 'Event is already booked'

const BOOKING_CONFLICT_MESSAGE =
  'This event is no longer available because another reporter booked it first. Please select another event.'

const isBookingConflict = (error: BookEventError): boolean => {
  return (
    error.response?.status === 409 ||
    (error.response?.status === 400 &&
      error.response?.data?.detail === PARROT_BOOKING_CONFLICT_DETAIL)
  )
}

export const getBookEventErrorMessage = (error: BookEventError): string => {
  if (isBookingConflict(error)) {
    return BOOKING_CONFLICT_MESSAGE
  }

  switch (error.response?.status) {
    case 400:
      return getApiErrorMessage(error, 'Could not book the event')
    case 403:
      return 'Authorization error. Please try logging out and back in, then try again. If the problem persists, please contact support.'
    case 404:
      return 'Could not book the event because it could not be found.'
    default:
      return 'Could not book the event. Please try again.'
  }
}

export const handleBookEventError = (
  error: BookEventError,
  options: BookEventErrorOptions,
) => {
  const detail = error.response?.data?.detail

  if (
    error.response?.status === 400 &&
    detail === 'Missing reporter settings' &&
    options.onReporterSettingsMissing
  ) {
    options.onReporterSettingsMissing()
    return
  }

  if (
    error.response?.status === 400 &&
    detail === 'Reporter commission will be expired' &&
    options.onReporterCommissionExpired
  ) {
    options.onReporterCommissionExpired()
    return
  }

  options.onErrorMessage(getBookEventErrorMessage(error))

  if (isBookingConflict(error)) {
    options.onBookingConflict?.()
  }
}

export const useBookMutation = (
  event: Event,
  options: BookEventErrorOptions & {
    onSuccess: () => void
  },
  params?: { book_related_events: boolean },
) =>
  useMutation((payload: CreateBookingPayload) => {
    return axios
      .post(event.booking_url, payload, { params })
      .then(options.onSuccess, error => {
        handleBookEventError(error, options)
      })
  })

export const useUnbookMutation = (
  event: Event,
  options: {
    onSuccess: () => void
    onErrorMessage: (error: string) => void
  },
  params?: { unbook_related_events: boolean },
) =>
  useMutation(() => {
    return axios
      .delete(event.booking_url, { params })
      .then(options.onSuccess, error => {
        if (error.response?.status === 400) {
          options.onErrorMessage(
            getApiErrorMessage(error, 'Could not unbook the event'),
          )
          return
        }

        throw error
      })
  })

export type AttendeeData = {
  name: string
  role: AttendeeRole
  email: string | null
  present: boolean
}

export type EventFeedback = {
  result: EventResult
  witness_requested_transcript: boolean
  read_waive_answer?: ReadWaiveAnswer
  transcript_ordered: boolean
  turnaround_hours: number | null
  cna_declared_at: string | null
  cna_type: string | null
  cna_type_other_text: string | null
  id_type: string | null
  id_number: string | null
  id_screenshots: UploadedFile[]
  timestamp_log: string | null
  additional_notes: string | null
  attendees: AttendeeData[]
  transcript_orders: TranscriptOrders | null
  // CR Form (staged) additions mapped for Parrot.
  spellings?: string | null
  witness_city?: string | null
  witness_state?: string | null
  // attendee name -> the Zoom participant(s) they joined as (mirrors DR Script).
  participants_mapping?: Record<string, ReporterFeedbackParticipantMapping[]>
}

export type ReporterFeedbackPayload = {
  reporter_state: string
  reporter_county: string
  events_feedbacks: EventFeedback[]
  steps?: CrFormStepsState
  sa?: SaScriptFields
  // Event-level CR Form note, flattened from `sa` so Parrot receives it on submit.
  record_preferences?: string
}

export const useReporterFeedbackQuery = (event: Event) =>
  useQuery<ReporterFeedbackPayload>(['reporter-feedback', event.id], () => {
    return axios
      .get(event.feedback_url)
      .then(r => r.data)
      .catch(e => {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          return
        }

        throw e
      })
  })

export const useSaveReporterFeedbackMutation = (
  event: Event,
  options: {
    onErrorMessage: (error: string) => void
  },
) =>
  useMutation(
    (payload: ReporterFeedbackPayload) => {
      return axios.put(event.feedback_url, payload).catch(error => {
        if (error.response?.status === 400) {
          options.onErrorMessage(
            getApiErrorMessage(error, 'Could not save reporter feedback'),
          )
          return
        }

        throw error
      })
    },
    { mutationKey: SAVE_REPORTER_FEEDBACK_MUTATION_KEY },
  )

export const useSubmitReporterFeedbackMutation = (
  event: Event,
  options: {
    onSuccess?: () => void
    onErrorMessage: (error: any) => void
  },
) =>
  useMutation((payload: ReporterFeedbackPayload) => {
    return axios
      .post(event.submit_feedback_url, payload)
      .then(options?.onSuccess, error => {
        if (error.response?.status === 400) {
          options.onErrorMessage(
            getApiErrorMessage(error, 'Could not submit reporter feedback'),
          )
          return
        }

        throw error
      })
  })

export const useEndEventMutation = (
  event: Event,
  options: {
    onErrorMessage: (error: any) => void
    onSuccess?: () => void
    onError?: () => void
  },
  params?: { end_related_events: boolean },
) =>
  useMutation(
    () => {
      return axios
        .post(event.end_url, undefined, {
          params,
        })
        .then(options?.onSuccess, error => {
          if (error.response?.status === 400) {
            options.onErrorMessage(
              getApiErrorMessage(error, 'Could not end the event'),
            )
            return
          }

          throw error
        })
    },
    { onError: options.onError },
  )

export const useCrNotJoinedMutation = (
  eventId: string,
  options: {
    onSuccess?: () => void
    onError?: () => void
  },
) =>
  useMutation(
    (payload: { resolved: boolean }) => crNotJoined(eventId, payload),
    options,
  )

export const useJoinInfoQuery = (
  event: Pick<Event, 'id' | 'join_info_url'>,
  options?: { enabled: boolean },
) =>
  useQuery<JoinInfo>(
    ['join-info', event.id],
    () => axios.get(event.join_info_url).then(r => r.data),
    options,
  )

export const useExhibitsQuery = (event: Pick<Event, 'id' | 'exhibits_url'>) =>
  useQuery<Exhibit[]>(['exhibits', event.id], () =>
    axios.get(event.exhibits_url).then(r => r.data),
  )

export const useMediaResourcesQuery = (
  event: Pick<Event, 'id' | 'media_resources_url'>,
) =>
  useQuery<MediaResource[]>(['media-resources', event.id], () =>
    axios.get(event.media_resources_url).then(r => r.data),
  )

export const useLowConfidenceWordsQuery = (
  event: Event,
  options?: Omit<
    UseQueryOptions<AsrWord[], AxiosError, AsrWord[]>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<AsrWord[], AxiosError, AsrWord[]>(
    ['low-confidence-words', event.id],
    () => axios.get(event.low_confidence_words_url).then(r => r.data),
    options,
  )
